import { RuleCondition } from "@/graphql/__generated__/graphql";
import { Badge } from "@/components/ui/badge";
import { percentageInputKeys, currencyInputKeys, listOfStringsToNotRender, monthSuffixComputationTypes } from "../constants";
import { formatNumberLocale, convertBpToDecimal } from "@/utils/helpers";
import { IntlShape } from "react-intl";
import { TFunction } from "i18next";
import { JSX } from "react";

/**
 * Props for the RenderConditionsComponent
 * @interface RenderConditionsComponentProps
 * @property {RuleCondition[]} ruleConditions - Array of rule conditions to render
 * @property {TFunction} t - Translation function
 * @property {IntlShape} intl - Internationalization object
 */
interface RenderConditionsComponentProps {
  ruleConditions: RuleCondition[];
  t: TFunction;
  intl: IntlShape;
}

/**
 * Renders a list of rule conditions
 * @param {RenderConditionsComponentProps} props - Component props
 * @returns {JSX.Element} Rendered component
 */
function RenderConditionsComponent({ ruleConditions, t, intl }: RenderConditionsComponentProps): JSX.Element {
  if (ruleConditions.length === 0) {
    return (
      <div>
        <p>{t("settings.rulebuilder.view.noconditions")}</p>
      </div>
    );
  }

  /**
   * Formats the condition value based on its computation type
   * @param {RuleCondition} condition - The rule condition to format
   * @returns {string} Formatted value
   */
  function renderFormattedValue(condition: RuleCondition) {
    if (percentageInputKeys.includes(condition.computationType)) {
      return `${convertBpToDecimal(Number(condition.value))}%`;
    }

    if (currencyInputKeys.includes(condition.computationType)) {
      return formatNumberLocale(intl, condition.value, "currency");
    }

    if (monthSuffixComputationTypes.includes(condition.computationType)) {
      return `${condition.value} ${t("settings.rulebuilder.months")}`;
    }

    return condition.value;
  }

  return (
    <div className="flex flex-col">
      {ruleConditions.map((condition, index) => (
        <div key={condition.id} className="flex items-center gap-2 hover:bg-blue-50 rounded-md px-2 py-1">
          {index === 0 ? (
            <span className="text-sm font-medium text-slate-500">{t("settings.rulebuilder.if")}</span>
          ) : (
            <span className="text-sm font-medium text-slate-500">{t("settings.rulebuilder.and")}</span>
          )}
          <Badge className="text-sm bg-blue-200 hover:bg-blue-100 text-slate-900">{t(`common:computationtype.${condition.computationType.toLowerCase()}`)}</Badge>
          <Badge className="text-sm bg-slate-200 hover:bg-slate-100 text-slate-900">{t(`common:operator.${condition.operator.toLowerCase()}`)}</Badge>
          {
            !listOfStringsToNotRender.includes(condition.value) && (
              <Badge className="text-sm bg-teal-200 hover:bg-teal-100 text-slate-900">
                {renderFormattedValue(condition)}
              </Badge>
            )
          }
        </div>
      ))}
    </div>
  );
}

export default RenderConditionsComponent;
